import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAdminAuth";
import Logo from "./Logo";
import {
  LayoutDashboard,
  Building2,
  Users,
  ScrollText,
  ArrowUpCircle,
  LogOut,
  LineChart,
  BookOpen,
  Receipt,
  Sparkles,
  TrendingUp,
  ToggleLeft,
  Package,
  ArrowRightLeft,
  Calculator,
  Globe,
} from "lucide-react";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/admin/tenants", icon: Building2, label: "Tenants" },
  { to: "/admin/users", icon: Users, label: "Users" },
  { to: "/admin/audit-log", icon: ScrollText, label: "Audit Log" },
  { to: "/admin/upgrade-requests", icon: ArrowUpCircle, label: "Upgrades" },
  { to: "/admin/market-watchers", icon: LineChart, label: "Market Watchers" },
];

const HO_FINANCE_NAV = [
  { to: "/admin/subscription-ledger", icon: BookOpen, label: "Subscription Ledger" },
  { to: "/admin/ho-expenses", icon: Receipt, label: "HO Expenses" },
  { to: "/admin/ai-profitability", icon: Sparkles, label: "AI Profitability" },
  { to: "/admin/pnl", icon: TrendingUp, label: "P&L Statement" },
];

const GOVERNANCE_NAV = [
  { to: "/admin/feature-overrides", icon: ToggleLeft, label: "Feature Overrides" },
  { to: "/admin/packages", icon: Package, label: "Packages & Products" },
  { to: "/admin/upgrade-tool", icon: ArrowRightLeft, label: "Upgrade Tool" },
  { to: "/admin/accounts-finance", icon: Calculator, label: "Accounts & Finance" },
  { to: "/admin/country-settings", icon: Globe, label: "Country Settings" },
];

function navItemClass({ isActive }) {
  return `flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all
    ${
      isActive
        ? "bg-[#7C5CFF]/10 text-[#7C5CFF] font-semibold"
        : "text-[#5B617A] hover:bg-[#7C5CFF]/[0.05] hover:text-[#0B0F1A]"
    }`;
}

export default function AppShell() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="admin-shell flex h-screen glass-bg">
      {/* Sidebar */}
      <aside
        className="hidden md:flex flex-col w-[270px] glass-sidebar shrink-0"
        data-testid="admin-sidebar"
      >
        <div className="px-5 py-4 border-b border-[#7C5CFF]/[0.08]">
          <Logo size="lg" theme="light" />
          <p className="text-[10px] text-[#5B617A] font-medium mt-1">Platform Console</p>
        </div>

        <nav className="flex-1 px-3 py-2 flex flex-col gap-0.5 overflow-y-auto">
          {NAV.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={navItemClass}
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}

          {/* HO Finance group */}
          <div className="mt-4 mb-1 px-3 text-[10px] font-bold uppercase tracking-wider text-[#5B617A]/60">
            HO Finance
          </div>
          {HO_FINANCE_NAV.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
              className={navItemClass}
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}

          {/* Governance group */}
          <div className="mt-4 mb-1 px-3 text-[10px] font-bold uppercase tracking-wider text-[#5B617A]/60">
            Governance
          </div>
          {GOVERNANCE_NAV.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={navItemClass}
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-[#7C5CFF]/[0.08]">
          <div className="flex items-center gap-2.5 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C5CFF] to-[#5A3FD9] flex items-center justify-center text-[10px] font-bold text-white">
              {admin?.name?.[0]?.toUpperCase() ?? "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-[#0B0F1A] truncate">{admin?.name}</p>
              <p className="text-[10px] text-[#5B617A] truncate">{admin?.email}</p>
            </div>
            <button
              onClick={doLogout}
              data-testid="logout-btn"
              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
              title="Log out"
            >
              <LogOut className="w-3.5 h-3.5 text-red-500" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Desktop top bar — robot avatar */}
        <div className="hidden md:flex items-center justify-end px-5 py-2 border-b border-[#7C5CFF]/[0.08] bg-white/60 backdrop-blur-sm">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#7C5CFF]/10 to-[#22D3EE]/8 border border-[#7C5CFF]/10 flex items-center justify-center overflow-hidden" data-testid="admin-robot-avatar" title="1000AD AI">
            <img src="/brand/bot_head.png" alt="AI Assistant" className="w-7 h-7 object-contain" />
          </div>
        </div>

        {/* Mobile header */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-white/80 backdrop-blur-xl border-b border-[#7C5CFF]/[0.08]">
          <div className="flex items-center gap-2">
            <Logo size="sm" theme="light" />
          </div>
          <button onClick={doLogout} data-testid="mobile-logout-btn" className="text-red-500">
            <LogOut className="w-4 h-4" />
          </button>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav
          className="md:hidden flex items-center justify-around bg-white/80 backdrop-blur-xl border-t border-[#7C5CFF]/[0.08] py-2"
          data-testid="mobile-nav"
        >
          {NAV.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] font-medium
                ${isActive ? "text-[#7C5CFF]" : "text-[#5B617A]/60"}`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
